import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { IonicModule, ToastController } from '@ionic/angular';
import { SupabaseService, UserRole } from 'src/app/services/supabase.service';
import { UploadManagerService } from 'src/app/services/upload-manager.service';
import { Subscription } from 'rxjs';

interface NavItem {
  title: string;
  url: string;
  icon: string;
  roles?: UserRole[];
}

const MOBILE_LAYOUT_QUERY = '(max-width: 768px)';

@Component({
  selector: 'app-admin-layout',
  standalone: true,
  imports: [CommonModule, FormsModule, IonicModule, RouterModule],
  templateUrl: './admin-layout.component.html',
  styleUrls: ['./admin-layout.component.scss'],
})
export class AdminLayoutComponent implements OnInit, OnDestroy {
  /** Desktop: the sidebar is reduced to icons. Mobile (<= 768px): the drawer is open. */
  isSidebarCollapsed = false;
  isMobileViewport = false;
  userProfile = {
    name: 'Admin User',
    email: 'admin@medmentor.com',
    role: 'Administrateur',
  };
  private authSubscription?: Subscription;
  private uploadSubscription?: Subscription;
  private routerSubscription?: Subscription;
  private mobileQuery?: MediaQueryList;
  private readonly onViewportChange = (event: MediaQueryListEvent | MediaQueryList) => { this.isMobileViewport = event.matches; };
  private isRedirectingToLogin = false;
  private isLoggingOut = false;
  userRole: UserRole | null = null;
  isPasswordDialogOpen = false;
  isChangingPassword = false;
  currentPassword = '';
  newPassword = '';
  confirmPassword = '';
  passwordError = '';

  activeUploadsCount = 0;
  overallUploadProgress = 0;

  readonly navItems: NavItem[] = [
    { title: 'Overview', url: '/dashboard', icon: 'grid-outline', roles: ['superadmin', 'admin'] },
    { title: 'Users', url: '/users', icon: 'people-outline', roles: ['superadmin', 'admin'] },
    { title: 'Activity log', url: '/activity-log', icon: 'time-outline', roles: ['superadmin', 'admin'] },
    { title: 'Modules', url: '/modules-management', icon: 'library-outline' },
    { title: 'Notes', url: '/notes', icon: 'document-text-outline' },
    { title: 'Attachments', url: '/attachments', icon: 'folder-open-outline' },
    { title: 'Arbre Décisionnel', url: '/decision-tree', icon: 'git-network-outline' },
   // { title: 'PDF Studio', url: '/pdf-studio', icon: 'document-outline' },
  ];

  constructor(
    private readonly supabaseService: SupabaseService,
    private readonly uploadManager: UploadManagerService,
    private readonly router: Router,
    private readonly toastCtrl: ToastController,
  ) {}

  async ngOnInit(): Promise<void> {
    if (typeof window !== 'undefined' && window.matchMedia) {
      this.mobileQuery = window.matchMedia(MOBILE_LAYOUT_QUERY);
      this.onViewportChange(this.mobileQuery);
      this.mobileQuery.addEventListener('change', this.onViewportChange);
    }
    // The mobile drawer covers the page, so close it once a destination has been chosen.
    this.routerSubscription = this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd && this.isMobileViewport) this.isSidebarCollapsed = false;
    });
    await this.loadUserProfile();
    this.authSubscription = this.supabaseService.currentUser$.subscribe((user) => {
      if (!user && !this.isLoggingOut) {
        void this.redirectToLogin();
      }
    });

    this.uploadSubscription = this.uploadManager.tasks$.subscribe((tasks) => {
      const active = tasks.filter((t) => t.status === 'uploading' || t.status === 'pending');
      this.activeUploadsCount = active.length;

      if (active.length > 0) {
        const totalProgress = active.reduce((acc, t) => acc + t.progress, 0);
        this.overallUploadProgress = Math.round(totalProgress / active.length);
      } else {
        this.overallUploadProgress = 0;
      }
    });
  }

  ngOnDestroy(): void {
    this.authSubscription?.unsubscribe();
    this.uploadSubscription?.unsubscribe();
    this.routerSubscription?.unsubscribe();
    this.mobileQuery?.removeEventListener('change', this.onViewportChange);
  }

  /** Labels and the account footer are hidden only for the collapsed desktop rail, never in the mobile drawer. */
  get showSidebarLabels(): boolean {
    return this.isMobileViewport || !this.isSidebarCollapsed;
  }

  get isMobileDrawerOpen(): boolean {
    return this.isMobileViewport && this.isSidebarCollapsed;
  }

  toggleSidebar(): void {
    this.isSidebarCollapsed = !this.isSidebarCollapsed;
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.isPasswordDialogOpen) this.closePasswordDialog();
    else if (this.isMobileDrawerOpen) this.isSidebarCollapsed = false;
  }

  openPasswordDialog(): void {
    this.currentPassword = '';
    this.newPassword = '';
    this.confirmPassword = '';
    this.passwordError = '';
    this.isPasswordDialogOpen = true;
    // On phones the drawer would otherwise stay open behind the dialog.
    if (this.isMobileViewport) this.isSidebarCollapsed = false;
  }

  closePasswordDialog(): void {
    if (this.isChangingPassword) return;
    this.isPasswordDialogOpen = false;
    this.passwordError = '';
  }

  async changeOwnPassword(): Promise<void> {
    this.passwordError = '';
    if (!this.currentPassword || !this.newPassword || !this.confirmPassword) {
      this.passwordError = 'Fill in all password fields.';
      return;
    }
    if (this.newPassword.length < 8) {
      this.passwordError = 'The new password must have at least 8 characters.';
      return;
    }
    if (this.newPassword !== this.confirmPassword) {
      this.passwordError = 'The new passwords do not match.';
      return;
    }
    if (this.currentPassword === this.newPassword) {
      this.passwordError = 'Choose a password different from the current one.';
      return;
    }

    this.isChangingPassword = true;
    try {
      const user = await this.supabaseService.getCurrentUser();
      if (!user?.email) throw new Error('Your account email is unavailable.');

      // Re-authenticate before changing a sensitive credential.
      await this.supabaseService.signIn(user.email, this.currentPassword);
      await this.supabaseService.updatePassword(this.newPassword);

      this.isPasswordDialogOpen = false;
      const toast = await this.toastCtrl.create({
        message: 'Password changed successfully.',
        duration: 2500,
        color: 'success',
        position: 'top',
      });
      await toast.present();
    } catch (error: any) {
      this.passwordError = error?.message || 'Unable to change the password.';
    } finally {
      this.isChangingPassword = false;
    }
  }

  get visibleNavItems(): NavItem[] {
    return this.navItems.filter((item) => {
      if (!item.roles) return true;
      if (!this.userRole) return false;
      if (this.userRole === 'superadmin') return true;
      return item.roles.includes(this.userRole);
    });
  }

  async logout(): Promise<void> {
    this.isLoggingOut = true;
    try {
      await this.supabaseService.signOut(false);
      await this.redirectToLogin();

      // Present the confirmation after the login route has rendered. This
      // avoids the route transition dismissing or visually clipping the toast.
      const toast = await this.toastCtrl.create({
        message: 'Déconnexion réussie',
        duration: 2000,
        color: 'success',
        position: 'top',
      });
      await toast.present();
    } catch (error) {
      console.error('Erreur de déconnexion:', error);
      await this.redirectToLogin();
    } finally {
      this.isLoggingOut = false;
    }
  }

  private async redirectToLogin(): Promise<void> {
    if (this.isRedirectingToLogin || this.router.url.startsWith('/login')) return;

    this.isRedirectingToLogin = true;
    const navigationSucceeded = await this.router.navigateByUrl('/login', { replaceUrl: true });

    // A hard redirect only runs if Ionic declined the route transition, ensuring
    // a signed-out administrator can never remain on a protected screen.
    if (!navigationSucceeded && !window.location.pathname.endsWith('/login')) {
      window.location.replace('/login');
    }
  }

  private async loadUserProfile(): Promise<void> {
    try {
      const user = await this.supabaseService.getCurrentUser();
      if (!user) return;

      this.userProfile.email = user.email || this.userProfile.email;
      this.userProfile.name = user.user_metadata?.['full_name'] || user.email?.split('@')[0] || 'Admin';
      const role = await this.supabaseService.getUserRole();
      if (role) {
        this.userRole = role;
        this.userProfile.role = role === 'superadmin' ? 'Super Administrateur 👑' : role === 'admin' ? 'Administrateur' : role === 'contributor' ? 'Contributeur' : 'Étudiant';
      }
    } catch (error) {
      console.error('Erreur lors du chargement du profil:', error);
    }
  }
}
